import React from 'react';
import { AlertOctagon, Clock, ShieldOff, XCircle } from 'lucide-react';
import { DepotLayout } from './DepotLayout';
import type { DepotBrandingDto, DepotSecuriseStatus } from '../types';

type TerminalErrorKind =
  | Exclude<DepotSecuriseStatus, 'Valide'>
  | 'NetworkError';

interface Props {
  kind: TerminalErrorKind;
  /** Message custom (utilisé pour NetworkError). */
  detail?: string;
  /**
   * Habillage courtier/produit. Souvent absent quand on tombe ici parce
   * que le back n'a pas pu identifier le souscripteur (token invalide),
   * mais affiché si disponible pour garder la cohérence visuelle.
   */
  branding?: DepotBrandingDto;
}

/**
 * Tableau de correspondance kind → icône / titre / explication.
 * Le ton reste neutre et oriente toujours vers le courtier (seule personne
 * capable de débloquer la situation côté Espace Pro).
 */
const CONTENT: Record<
  TerminalErrorKind,
  {
    icon: React.ElementType;
    title: string;
    description: string;
    iconColor: string;
    iconBg: string;
  }
> = {
  Introuvable: {
    icon: XCircle,
    title: 'Lien introuvable',
    description:
      "Ce lien n'existe pas ou a déjà été utilisé. Si vous pensez qu'il s'agit d'une erreur, contactez votre courtier qui pourra vous en transmettre un nouveau.",
    iconColor: 'text-red-500',
    iconBg: 'bg-red-50'
  },
  Expire: {
    icon: Clock,
    title: 'Lien expiré',
    description:
      "Ce lien n'est plus valable (30 jours dépassés). Contactez votre courtier pour en recevoir un nouveau.",
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-50'
  },
  Bloque: {
    icon: ShieldOff,
    title: 'Lien bloqué',
    description:
      "Trop de tentatives de date de naissance incorrectes. Pour des raisons de sécurité, ce lien est désormais bloqué. Contactez votre courtier pour en recevoir un nouveau.",
    iconColor: 'text-red-500',
    iconBg: 'bg-red-50'
  },
  NetworkError: {
    icon: AlertOctagon,
    title: 'Connexion impossible',
    description:
      'Nous ne parvenons pas à joindre le serveur. Vérifiez votre connexion internet puis rafraîchissez la page.',
    iconColor: 'text-gray-500',
    iconBg: 'bg-gray-100'
  }
};

export const DepotErrorScreen: React.FC<Props> = ({
  kind,
  detail,
  branding
}) => {
  const { icon: Icon, title, description, iconColor, iconBg } = CONTENT[kind];

  return (
    <DepotLayout branding={branding}>
      <div className="flex flex-col items-center text-center gap-5">
        <div className={`${iconBg} rounded-full p-4`}>
          <Icon className={`w-16 h-16 ${iconColor}`} strokeWidth={2} />
        </div>
        <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
        <p className="text-gray-600 leading-relaxed">{detail ?? description}</p>
      </div>
    </DepotLayout>
  );
};
